import { useCallback, useEffect, useMemo, useState } from "react";
import { ShippingRecordDispute, Media } from "$types";
import useAuth from "./useAuth";
import useMedia from "./useMedia";
import { ShippingRecordDisputeService } from "$services";

const useShippingRecordDispute = (shippingRecordDisputeId: string) => {
  const { token } = useAuth();

  // Fetching
  const [shippingRecordDispute, setShippingRecord] =
    useState<ShippingRecordDispute>(null);
  const [isFetching, setIsFetching] = useState(false);
  const fetchItem = useCallback(
    async (id: string) => {
      try {
        setIsFetching(true);
        const result =
          await ShippingRecordDisputeService.getShippingRecorDispute(
            token,
            shippingRecordDisputeId ?? id
          );
        setShippingRecord(result);
      } catch (error: any) {
        console.error("Error fetching shipping record:", error);
      } finally {
        setIsFetching(false);
      }
    },
    [shippingRecordDisputeId, setShippingRecord, setIsFetching, token]
  );

  // Launch fetching
  useEffect(() => {
    setShippingRecord(null);
    if (shippingRecordDisputeId) {
      fetchItem(shippingRecordDisputeId);
    }
  }, [fetchItem, setShippingRecord, shippingRecordDisputeId]);

  // Update
  const updateShippingRecordDispute = useCallback(
    async (shippingRecordId: string, data: any) => {
      const result =
        await ShippingRecordDisputeService.updateShippingRecorDispute(
          token,
          shippingRecordId,
          data
        );
      setShippingRecord(result);
    },
    [setShippingRecord, token]
  );

  // Create
  const createShippingRecordDispute = useCallback(
    async (data: any) => {
      const result =
        await ShippingRecordDisputeService.createShippingRecorDispute(
          token,
          data
        );
      setShippingRecord(result);
    },
    [setShippingRecord, token]
  );

  const [media, setMedia] = useState<Media[]>([]);
  const { getMedia } = useMedia("shipping");

  const fetchFiles = useCallback(async () => {
    if (shippingRecordDispute && shippingRecordDispute.files) {
      const filesFromApi = await Promise.all(
        shippingRecordDispute.files
          .filter((file) => file.mainApiMediaId)
          .map((file) => getMedia(file.mainApiMediaId))
      );
      setMedia(filesFromApi);
    }
  }, [shippingRecordDispute, getMedia]);

  useEffect(() => {
    if (shippingRecordDispute) {
      fetchFiles();
    }
  }, [shippingRecordDispute, fetchFiles]);

  const populatedShippingRecordDispute = useMemo(
    () => (shippingRecordDispute ? { ...shippingRecordDispute, media } : null),
    [shippingRecordDispute, media]
  );

  return {
    shippingRecordDispute: populatedShippingRecordDispute,
    createShippingRecordDispute,
    updateShippingRecordDispute,
    isFetching,
  };
};

export default useShippingRecordDispute;
